/**
 * Strip credentials and personal data out of anything on its way to a report.
 *
 * Telemetry is a classic data-leak path: nobody intends to send a session
 * token to a log, and then an error message interpolates a URL that has one in
 * the query string. This runs over every string in a report — message, stack,
 * every breadcrumb — before it leaves the device.
 *
 * ## What this is not
 *
 * It is not a substitute for not putting secrets in messages. It is the second
 * layer, on the assumption that the first one will be got wrong eventually, by
 * someone adding a `throw new Error(\`GET \${url} failed\`)` without thinking
 * about what is in `url`.
 *
 * ## The trade being made
 *
 * Redaction destroys diagnostic value. `[redacted]` in the middle of a stack
 * trace is a fact you no longer have. So the patterns below are deliberately
 * specific — named credential parameters, recognisable token shapes, email
 * addresses — rather than "anything long and random", which would eat file
 * hashes, request ids and bundle names and leave reports unreadable.
 *
 * The one place that errs toward over-redaction is the long opaque token. AEGIS
 * holds several vendor API keys that look like nothing else, and a leaked one is
 * spendable by whoever finds it. A redacted checksum is a smaller loss.
 */

const PLACEHOLDER = '[redacted]';

/**
 * Each rule keeps its own comment because the reason a pattern exists is not
 * recoverable from the pattern.
 */
const RULES: readonly (readonly [RegExp, string])[] = [
  // `Authorization: Bearer <token>`. The scheme is kept so the reader can see
  // that authentication was attempted at all, which is often the question.
  [/\b(Bearer|Basic|Token)\s+[A-Za-z0-9\-._~+/=]{8,}/gi, `$1 ${PLACEHOLDER}`],

  // A JWT anywhere. Three base64url segments — distinctive enough to match on
  // shape, and a session token in this codebase can appear in a header dump.
  [/\beyJ[A-Za-z0-9\-_]{4,}\.[A-Za-z0-9\-_]{4,}\.[A-Za-z0-9\-_]{4,}/g, PLACEHOLDER],

  // Named credential parameters in a query string or a form body. The name is
  // preserved and only the value goes, so `?api_key=[redacted]` still tells
  // you which parameter was involved.
  [
    /\b((?:api[-_]?key|apikey|access[-_]?token|auth[-_]?token|session|secret|password|passwd|pwd|token|key)=)[^&\s"'`]+/gi,
    `$1${PLACEHOLDER}`,
  ],

  // JSON fields with the same names. `"password":"hunter2"` reaches a report
  // whenever a request body is stringified into an error.
  [
    /("(?:api[-_]?key|apiKey|accessToken|authToken|session|secret|password|token|key)"\s*:\s*)"[^"]*"/gi,
    `$1"${PLACEHOLDER}"`,
  ],

  // Stripe-shaped keys, live and test. Distinctive prefixes, and a leaked live
  // secret key is the worst single credential in the system.
  [/\b(sk|pk|rk|whsec)_(live|test)_[A-Za-z0-9]{8,}/g, PLACEHOLDER],

  // A long opaque token — 20+ alphanumeric characters containing BOTH a letter
  // and a digit.
  //
  // This has been wrong twice, each time in a way that looked fine.
  //
  // It was `[0-9a-f]{32,}`, which only caught hex: EIA, Finnhub and Marketaux
  // all issue mixed alphanumeric keys, so three of five vendor keys would have
  // travelled unredacted. Widening to alphanumeric fixed two of those three.
  //
  // It then said 32, and a Finnhub token is **20 characters**
  // (`c9pk3aad3i8s0l1nlq10`), so the key this rule's own comment named as the
  // reason for the previous fix still leaked. Nothing caught that either,
  // because the tests used invented strings rather than the real shapes; it
  // took running the actual key lengths through it to see.
  //
  // 20 rather than 32 costs nothing measurable. Run over this repo's own
  // stack frames, bundle names, fingerprints, instrument codes and messages,
  // the two thresholds redact exactly the same prose — a base64 image prefix
  // and a git sha, both already gone at 32 — and 20 additionally catches every
  // real key shape tested.
  //
  // The two lookaheads are what keep it off prose at all: a 20-letter word has
  // no digit and a 20-digit number has no letter, so neither matches.
  [/\b(?=[A-Za-z0-9]*\d)(?=[A-Za-z0-9]*[A-Za-z])[A-Za-z0-9]{20,}\b/g, PLACEHOLDER],

  // An all-hex run with no digits in it — `abcdef...` — which the rule above
  // cannot see because it requires a digit.
  [/\b[a-f]{32,}\b/gi, PLACEHOLDER],

  // Email addresses. PII, and never diagnostic: an error is about a code path,
  // not about which account walked into it. The server already knows the
  // account when the report arrives authenticated.
  [/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, '[email]'],
];

/** Run every rule over one string. Safe on any input, including empty. */
export function redact(text: string): string {
  let out = text;
  for (const [pattern, replacement] of RULES) out = out.replace(pattern, replacement);
  return out;
}

/**
 * The longest string a report may carry, per field.
 *
 * A stack trace from a minified bundle can run to tens of kilobytes, and a
 * device on a metered connection should not spend that on a crash report. The
 * head is kept rather than the tail: the frames nearest the throw are the ones
 * that identify it.
 */
export const MAX_FIELD = 8_000;

export function clamp(text: string, max = MAX_FIELD): string {
  return text.length <= max ? text : `${text.slice(0, max)}\n… ${text.length - max} more characters`;
}

/** Redact and clamp in the order that matters: redact first, then truncate. */
export function scrub(text: string, max = MAX_FIELD): string {
  // Redaction must run on the whole string. Truncating first could cut a token
  // in half and leave a prefix that no rule matches — and half a live key is
  // still most of a live key.
  return clamp(redact(text), max);
}
